import React, { useEffect, useState } from "react"
import { useTheme } from '~/contexts/ThemeContext'
import { notBlank, imgSize } from '~/utils/helpers'
import useAnalytics from '~/utils/analytics'
import VideoBg from '~/components/video/bg'

import { aria_btn, cta_link } from "~/styles/app.module.scss"
import { notice } from "~/styles/stores/our_stores.module.scss"
import * as styles from "~/styles/stores/store_detail.module.scss"

const StoreDetail = ({ data }) => {
  const { locate, setLocate } = useTheme()
  const { ga_store_toggle } = useAnalytics()
  const [didLoad, setDidLoad] = useState(null)

  const store = data
  const store_title = !store.title.includes('Buck Mason') ? `Buck Mason ${store.title}` : store.title

  const vid_opts = (item) => {
    const options = {
      autoplay: true,
      controls: false,
      children: ['mediaLoader', 'posterImage'],
      setPlaysinline: true,
      playsinline: true,
      muted: true,
      loop: true,
      sources: item.sources ? item.sources.filter(x => x.mimeType !== 'application/x-mpegURL').map((x) => {
        return {src: x.url, type: x.mimeType}
      }) : []
    }
    return options
  }

  const handleStore = (short_name) => {
    let short_names = []
    if ( locate.currStores.includes(store.short_name) ) {
      ga_store_toggle('disable', store.title)
    } else {
      short_names = [store.short_name]
      ga_store_toggle('enable', store.title)
    }

    let store_gend = false
    if (!(store.avail_mens && store.avail_womens)) {
      store_gend = store.avail_mens ? 'm' : 'w'
    }
    setLocate({
      ...locate,
      currStores: short_names,
      storeGend: store_gend
    })
  }
  
  const feats = store.avail_mens 
                || store.avail_womens 
                || store.avail_coffee 
                || store.avail_watches
                || store.pickup_enabled

  const top_img = notBlank(store.detail_image_col_1) ? store.detail_image_col_1 : notBlank(store.metaimg) ? store.metaimg : false
  const has_hero_vid = notBlank(store.hero_desk_video) || notBlank(store.hero_mob_video)

  useEffect(() => {
    if (!locate) return

    setDidLoad(true)
  }, [locate])

  return (
    <>
      <section 
        className={styles.store}
      >
        <div className={[styles.cols, styles.info].join(' ')}>
          <div className={[styles.col, styles.address].join(' ')}>
            <h1 className={[styles.title, styles.name].join(' ')}>
              {store_title}
            </h1>
            <div className={styles.shop}>
              <button 
                type="button"
                className={`${aria_btn} ${cta_link}`} 
                onClick={(e)=>{handleStore(store)}}
              >
                {(didLoad && !locate?.currStores.includes(store.short_name)) ? 'Shop Store' : 'Stop Shopping'}
              </button>
              {(didLoad && locate?.currStores.includes(store.short_name)) && (
                <div className={notice}>
                  When you browse our collections, all products will be filtered by this store.
                </div>
              )}
            </div>

            {(notBlank(store.address_line_1) || notBlank(store.address_city)) && (
              <>
                <h2 className={styles.title}>
                  Address
                </h2>
                {notBlank(store.address_line_1) && (
                  <p>
                    {store.address_line_1}
                  </p>
                )}
                {notBlank(store.address_line_2) && (
                  <p>
                    {store.address_line_2}
                  </p>
                )}
                {notBlank(store.address_city) && (
                  <p>
                    {store.address_city}, {store.address_state} {store.address_zip}
                  </p>
                )}

                <div className={styles.map_link}>
                  {notBlank(store.gmaps) && (
                    <a 
                      className={cta_link}
                      href={`https://www.google.com/maps/dir/?api=1&destination=${store.gmaps}`} 
                      rel="noreferrer" 
                      target="_blank"
                    >
                      Get Directions
                    </a>
                  )}
                </div>
              </>
            )}

            {notBlank(store.hours) && (
              <>
                <h2 className={styles.title}>
                  Hours
                </h2>
                <p>
                  {store.hours.split('\n').map( (time,index) => 
                    <React.Fragment key={`${store.short_name}}time${index}`}>
                      {time}
                      <br/>
                    </React.Fragment>
                  )}
                </p>
              </>
            )}

            <h2 className={styles.title}>
              Contact
            </h2>

            {notBlank(store.phone) && (
              <p>
                <a href={`tel:${store.phone.replaceAll('.', '').replaceAll('-', '')}`}>
                  {store.phone}
                </a>
              </p>
            )}
            <p>
              <a href="mailto:help@buckmason.com" className={aria_btn} target="_blank" rel="noreferrer">
                help@buckmason.com
              </a>
            </p>

            {feats && (
              <>
                <h2 className={styles.title}>
                  At This Store
                </h2>
                {store.avail_mens && (
                  <p>
                    Men's
                  </p>
                )}
                {store.avail_womens && (
                  <p>
                    Women's
                  </p>
                )}
                {store.pickup_enabled && (
                  <p>
                    In-Store Pickup
                  </p>
                )}
                {store.avail_watches && (
                  <p>
                    Vintage Watches
                  </p>
                )}
                {store.avail_coffee && (
                  <p>
                    <a href="https://www.fasttimescoffee.com" target="_blank" rel="noreferrer">
                      Fast Times Coffee
                    </a>
                  </p>
                )}
              </>
            )}

          </div>
          <div className={[styles.col, styles.mapwr].join(' ')}>
            <>
              {notBlank(store.top_video) && (
                <div className={styles.col}>
                  <VideoBg 
                    options={vid_opts(store.top_video)}
                    vidclasses={`${styles.img}`}
                    off={false}
                  />
                </div>
              )}
              {(top_img && !notBlank(store.top_video)) && (
                <div className={styles.col}>
                  <img className={styles.img} src={imgSize(top_img,900)} alt="" />
                </div>
              )}
            </>
          </div>
        </div>


        {notBlank(store.intro_message) && (           
          <div 
            className={styles.desc}
            dangerouslySetInnerHTML={{ __html: store.intro_message }}
          />
        )}

        {(notBlank(store.image_left) && notBlank(store.image_right)) && (           
          <div className={styles.store_imgcols}>
            <div className={styles.store_imgcol}>
              <img className={styles.img} src={imgSize(store.image_left,900)} alt="" />
            </div>
            <div className={styles.store_imgcol}>
              <img className={styles.img} src={imgSize(store.image_right,900)} alt="" />
            </div>
          </div>
        )}

        {has_hero_vid && (
          <div className={styles.full_image}>
            <>
              {notBlank(store.hero_desk_video) && (
                <VideoBg 
                  options={vid_opts(store.hero_desk_video)}
                  vidclasses={[styles.img, styles.desk_vid].join(' ')}
                  off={false}
                />
              )}
              {notBlank(store.hero_mob_video) && (
                <VideoBg 
                  options={vid_opts(store.hero_mob_video)}
                  vidclasses={[styles.img, styles.mob_vid].join(' ')}
                  off={false}
                />
              )}
            </>
          </div>
        )}
        {(notBlank(store.detail_hero_image && !has_hero_vid)) && (
          <div className={styles.full_image}>
            <img 
              className={styles.img}
              src={imgSize(store.detail_hero_image,1400)}
              width="1900" 
              height="725" 
              alt=""
            />
          </div>
        )}

      </section>

    </>
  )
}

export default StoreDetail

